import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MessageCircle, ArrowRight, Heart, DollarSign, BookOpen, Users } from 'lucide-react';
import { telehealthServices } from '../data/telehealthServices';
import './About.css';

const values = [
  {
    number: '01',
    icon: Heart,
    accent: '#d04a02',
    title: 'Accessibility',
    description:
      'Care should fit your life — not the other way around. Telehealth removes the commute, the waiting room, and rigid clinic hours whenever it is safe and appropriate.',
    image: '/images/about-accessibility.jpg',
  },
  {
    number: '02',
    icon: DollarSign,
    accent: '#0076a8',
    title: 'Affordability',
    description:
      'Our model reduces overhead so we can offer high-quality visits at clear, published rates — including options for patients without traditional insurance.',
    image: '/images/about-affordability.jpg',
  },
  {
    number: '03',
    icon: BookOpen,
    accent: '#d04a02',
    title: 'Education',
    description:
      'Understanding your condition and your labs empowers better decisions. We prioritize teaching and follow-up so you feel confident managing your health.',
    image: '/images/about-education.jpg',
  },
  {
    number: '04',
    icon: Users,
    accent: '#0076a8',
    title: 'Partnership',
    description:
      'We are here to be your partner in health — responsive by call, email, or text — supporting you between visits when you have questions.',
    image: '/images/about-partnership.jpg',
  },
];

const pricingRows = [
  { service: 'Non-emergent sick visits', fee: '$25/visit' },
  { service: 'Medication refills', fee: '$25/visit' },
  { service: 'Lab order & interpretation', fee: '$25' },
  { service: 'Chronic condition management', fee: '$50/visit' },
  { service: 'Weight loss management', fee: '$50/month' },
  { service: 'Smoking cessation', fee: '$25' },
];

const About = () => {
  return (
    <div className="about-page">

      {/* ── Page hero ──────────────────────────────────────────────────── */}
      <section className="about-hero">
        <img src="/images/about-mission-meeting.webp" alt="" className="about-hero-bg-img" />
        <div className="about-hero-overlay" />
        <div className="about-hero-inner">
          <motion.div
            className="about-hero-eyebrow"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <span className="about-hero-dot" />
            Open Arms Healthcare Services LLC
          </motion.div>
          <motion.h1
            className="about-hero-title"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Care that feels like <span className="about-hero-accent">family</span>
          </motion.h1>
          <motion.p
            className="about-hero-sub"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
          >
            A telehealth provider built around the belief that quality care should be
            accessible, convenient, and affordable — wherever you are.
          </motion.p>

          <motion.div
            className="about-hero-stats"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
          >
            <div className="about-hero-stat">
              <span className="about-hero-stat-num">6</span>
              <span className="about-hero-stat-label">Services</span>
            </div>
            <div className="about-hero-stat-sep" />
            <div className="about-hero-stat">
              <span className="about-hero-stat-num">$25</span>
              <span className="about-hero-stat-label">Starting price</span>
            </div>
            <div className="about-hero-stat-sep" />
            <div className="about-hero-stat">
              <span className="about-hero-stat-num">100%</span>
              <span className="about-hero-stat-label">Telehealth</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Who we are ─────────────────────────────────────────────────── */}
      <section className="about-intro">
        <div className="about-container about-intro-grid">
          <motion.div
            className="about-intro-text"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="section-eyebrow">Who we are</span>
            <h2 className="section-heading">Open Arms Healthcare Services LLC</h2>
            <p className="section-body">
              At Open Arms Healthcare Services LLC, we believe that quality healthcare should be
              accessible, convenient, and affordable for everyone, regardless of schedule or
              location. As a dedicated telehealth provider, we bring compassionate and
              comprehensive medical care directly to you, wherever you are.
            </p>
            <p className="section-body" style={{ marginTop: '1rem' }}>
              Our approach helps make managing your health a seamless part of your life — not a
              burden. Life is busy, and your health should not have to wait.
            </p>
          </motion.div>
          <motion.div
            className="about-intro-image"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <img
              src="/images/about-vision-aerial.webp"
              alt="Connected care from anywhere"
              className="intro-img"
            />
            <div className="intro-img-badge">
              <span className="intro-img-badge-text">Healthcare on your terms</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Values ─────────────────────────────────────────────────────── */}
      <section className="about-values">
        <div className="about-container">
          <motion.div
            className="values-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="section-eyebrow">Our principles</span>
            <h2 className="section-heading">What drives us</h2>
          </motion.div>

        <div className="values-rows">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              className={`value-row ${i % 2 !== 0 ? 'value-row--reversed' : ''}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <div className="value-row-img-wrap">
                <img src={v.image} alt={v.title} className="value-row-img" />
                <div
                  className="value-row-img-overlay"
                  style={{ background: `linear-gradient(135deg, ${v.accent}cc 0%, ${v.accent}44 100%)` }}
                />
                <span className="value-row-num">{v.number}</span>
              </div>
              <div className="value-row-content">
                <div className="value-row-icon" style={{ background: `${v.accent}12`, color: v.accent }}>
                  <v.icon size={20} />
                </div>
                <h3 className="value-row-title" style={{ color: v.accent }}>{v.title}</h3>
                <p className="value-row-desc">{v.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
        </div>
      </section>

      {/* ── Mission — two-tone split ────────────────────────────────────── */}
      <section className="about-mission-section">
        <div className="about-container">
        <div className="about-mission">
        <motion.div
          className="mission-left"
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <span className="section-eyebrow" style={{ color: 'rgba(208,74,2,0.85)' }}>Our mission</span>
          <h2 className="mission-headline">Breaking down barriers to great care</h2>
          <blockquote className="mission-quote">
            "To make it easier for people from all walks of life to access safe, fast, and
            affordable healthcare — at their convenience, wherever they are."
          </blockquote>
          <p className="mission-body-text">
            We are committed to breaking down traditional barriers by leveraging telehealth so
            expert medical advice and support are always within reach.
          </p>
        </motion.div>

        <motion.div
          className="mission-right"
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <span className="section-eyebrow">Our goal</span>
          <h2 className="mission-right-title">Improving outcomes, one visit at a time</h2>
          <div className="goal-items">
            {[
              {
                label: 'Manage chronic conditions',
                desc: 'Personalized telehealth visits that equip you with knowledge and tools for long-term health.',
              },
              {
                label: 'Cost-effective access',
                desc: 'A model that reduces overhead so high-quality care stays affordable, with or without insurance.',
              },
              {
                label: 'Better patient experience',
                desc: 'Flexible scheduling and direct communication by call, email, or text — less stress, more care.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                className="goal-item"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.12 }}
              >
                <span className="goal-item-num">0{i + 1}</span>
                <div>
                  <h4 className="goal-item-label">{item.label}</h4>
                  <p className="goal-item-desc">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        </div>
        </div>
      </section>

      {/* ── Services ───────────────────────────────────────────────────── */}
      <section className="about-services">
        <div className="about-container">
          <motion.div
            className="section-header-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="section-eyebrow">What we offer</span>
            <h2 className="section-heading centered">Our telehealth services</h2>
            <p className="section-sub">
              All services delivered via telehealth. Visit the{' '}
              <Link to="/services">services page</Link> for a full overview.
            </p>
          </motion.div>

          <div className="services-grid">
            {telehealthServices.map((s, i) => (
              <motion.div
                key={s.slug}
                className="service-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -5 }}
              >
                <div className="service-card-img-wrap">
                  <img src={s.cardImage} alt={s.title} className="service-card-img" />
                  <span className="service-card-price">{s.fee}</span>
                </div>
                <div className="service-card-body">
                  <h3 className="service-card-title">{s.title}</h3>
                  <p className="service-card-summary">{s.summary}</p>
                  <Link to={`/services/${s.slug}`} className="service-card-link">
                    Learn more <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────────────────── */}
      <section className="about-pricing">
        <div className="about-container">
          <motion.div
            className="section-header-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="section-eyebrow">No surprises</span>
            <h2 className="section-heading centered">Transparent pricing</h2>
            <p className="section-sub">
              Fees below are for telehealth services only and{' '}
              <strong>do not include medication or outside lab costs</strong>.
            </p>
          </motion.div>

          <motion.div
            className="pricing-table-wrap"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <table className="pricing-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Fee</th>
                </tr>
              </thead>
              <tbody>
                {pricingRows.map((row, i) => (
                  <tr key={row.service} className={i % 2 === 0 ? 'row-even' : ''}>
                    <td>{row.service}</td>
                    <td className="fee-cell">{row.fee}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          <p className="pricing-footer">
            We are here to be your partner in health.{' '}
            <Link to="/contact">Contact us today</Link> to get started.
          </p>
        </div>
      </section>

      {/* ── Reach us ───────────────────────────────────────────────────── */}
      <section className="about-reach">
        <div className="about-container">
          <motion.div
            className="section-header-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="section-eyebrow light">Get in touch</span>
            <h2 className="section-heading centered light">How to reach us</h2>
            <p className="section-sub light">
              Connect for scheduling, questions, or help choosing the right visit. We respond to
              calls, emails, and texts.
            </p>
          </motion.div>

          <div className="reach-channels">
            {[
              { icon: Phone, label: 'Call us', desc: 'Schedule or ask questions by phone.' },
              { icon: Mail, label: 'Email us', desc: 'Write to us anytime — we will respond.' },
              { icon: MessageCircle, label: 'Text us', desc: 'Quick coordination when available.' },
            ].map(({ icon: Icon, label, desc }, i) => (
              <motion.div
                key={label}
                className="reach-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="reach-icon">
                  <Icon size={22} />
                </div>
                <h3 className="reach-label">{label}</h3>
                <p className="reach-desc">{desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="reach-cta"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Link to="/contact" className="reach-btn">
              Go to contact form <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default About;
